import { Box, Text } from '@moonpig/launchpad-components'
import React, { memo, useEffect, useState } from 'react'
import { Selector, SelectorContent } from '@moonpig/launchpad-forms'
import { styled } from '@moonpig/launchpad-utils'
import { spacingPx } from '@moonpig/launchpad-theme'
import { system as s } from '@moonpig/launchpad-system'
import { useProductsLocaleText } from '../../locale'
import { useBasket } from '../../contexts/basket'
import { SelectAmountWarning } from './SelectAmountWarning'

const Grid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${spacingPx(5)};
  margin-top: ${spacingPx(6)};
`

const StyledBox = styled(Box)`
  > label {
    &:not(.lp-selector-disabled) {
      &.lp-selector-checked {
        ${s({
          border: '2px solid',
          borderColor: 'colorInteractionSelectedState',
          color: 'colorInteractionSelectedState',
          typography: 'typeButtonLabel',
        })}
      }
    }
  }
`

const StyledFieldSet = styled.fieldset`
  border-width: 0px;
  ${s({ paddingX: 0 })}
`

type Variant = {
  price: string
  sku: string
}

type PriceVariantSelectorProps = {
  variants: Variant[]
  selectedValue?: string
  onChange: (variantSku: string) => void
}

export const PriceVariantSelector: React.FC<PriceVariantSelectorProps> = memo(
  ({ variants, onChange, selectedValue }) => {
    const t = useProductsLocaleText()
    const [clickedVariant, setClickedVariant] = useState(selectedValue)
    const [state, { setSelectedAmountWarning }] = useBasket()
    const { selectedAmountWarning } = state
    const [slideOut, setSlideOut] = useState(false)
    const selectorRef = React.useRef<HTMLInputElement>(null)

    useEffect(() => {
      if (!selectedAmountWarning) {
        setSlideOut(false)
      }
    }, [selectedAmountWarning, slideOut])

    const handleOnChange = (variant: string) => {
      setClickedVariant(variant)
      onChange(variant)
      setSlideOut(true)
    }

    return (
      <>
        <StyledFieldSet>
          <Text typography="typeBodyLabel" as="legend">
            {t('variant_selector.select_amount')}
          </Text>
          <Grid>
            {variants.map(variant => {
              const variantId = `default-${variant.price}-option-plain`
              return (
                <StyledBox key={variant.sku}>
                  <Selector
                    id={variantId}
                    type="plain"
                    name="variant-selector"
                    value={variant.sku}
                    checked={Boolean(clickedVariant === variant.sku)}
                    onChange={() => handleOnChange(variant.sku)}
                    ref={selectorRef}
                  >
                    <SelectorContent
                      title={<Box textAlign="center">{variant.price}</Box>}
                    />
                  </Selector>
                </StyledBox>
              )
            })}
          </Grid>
        </StyledFieldSet>
        <SelectAmountWarning
          slideOut={slideOut}
          selectedAmountWarning={selectedAmountWarning}
          setSlideOut={setSlideOut}
          setSelectedAmountWarning={setSelectedAmountWarning}
          triggerRef={selectorRef}
        />
      </>
    )
  },
)
